import { saveAs } from 'file-saver';
import { Download, Info, X } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { DataTable } from 'components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Routes } from 'config/constants';

import { columns } from './columns';
import { CommissionDetailSheet } from './components/CommissionDetailSheet';
import {
  COMMISSIONS_PAGE_SIZE,
  CommissionItem,
  CommissionStatus,
  fetchAllCommissions,
  useCommission,
  useCommissions,
} from './hooks/useCommissions';

const ALL = 'all' as const;

const STATUS_OPTIONS: { value: CommissionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'updated', label: 'Updated' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'expired', label: 'Expired' },
];

const Page = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | typeof ALL>(ALL);
  const [invoiceId, setInvoiceId] = useQueryState('invoiceId', parseAsString);
  const [pageSize, setPageSize] = useState(COMMISSIONS_PAGE_SIZE);
  const [downloading, setDownloading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<CommissionItem | undefined>();

  const filters = {
    status: statusFilter === ALL ? undefined : statusFilter,
    invoiceId: invoiceId ?? undefined,
  };

  const {
    commissions,
    pageIndex,
    hasNextPage,
    hasPreviousPage,
    loading,
    error,
    nextPage,
    previousPage,
    resetPage,
  } = useCommissions(filters, pageSize);

  useEffect(() => {
    resetPage();
    // Reset cursor stack when filters or page size change.
  }, [statusFilter, pageSize, invoiceId]);

  const { commission: deepLinkedCommission, error: deepLinkError } = useCommission(id);

  useEffect(() => {
    if (deepLinkedCommission) {
      setSelectedCommission(deepLinkedCommission);
      setSheetOpen(true);
    }
  }, [deepLinkedCommission]);

  useEffect(() => {
    if (deepLinkError) {
      navigate(Routes.Commissions);
    }
  }, [deepLinkError, navigate]);

  const handleSheetOpen = (open: boolean) => {
    setSheetOpen(open);
    if (!open && id) {
      // Navigate back to list when closing sheet on deeplink.
      navigate(Routes.Commissions);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const all = await fetchAllCommissions(filters);
      const rows = [
        [
          'Date',
          'Checkout Intent ID',
          'Type',
          'Status',
          'Direction',
          'Commission',
          'Rye fee',
          'Currency',
          'Commission ID',
        ],
        ...all.map((c: CommissionItem) => [
          new Date(c.createdAt).toISOString().split('T')[0],
          c.checkoutIntentId,
          c.type,
          c.status,
          c.settlementDirection,
          (c.developerCommission.amountSubunits / 100).toFixed(2),
          (c.ryeFee.amountSubunits / 100).toFixed(2),
          c.developerCommission.currencyCode,
          c.id,
        ]),
      ];
      const csv = rows
        .map((r) =>
          r
            .map((cell) => {
              const safe = String(cell ?? '').replace(/^[=+\-@]/, "'$&");
              return `"${safe.replace(/"/g, '""')}"`;
            })
            .join(','),
        )
        .join('\n');
      saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'commissions.csv');
    } catch {
      alert('Failed to download commissions. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <CommissionDetailSheet
        open={sheetOpen}
        onOpenChange={handleSheetOpen}
        commission={selectedCommission}
      />
      <Card className="mt-[28px]">
        <CardHeader className="flex-auto flex-row justify-between">
          <div className="space-y-1">
            <CardTitle className="whitespace-break-spaces leading-[30px]">Commissions</CardTitle>
            <CardDescription>Commissions earned from completed checkout intents</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as CommissionStatus | typeof ALL)}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All statuses</SelectItem>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {invoiceId ? (
                <Badge
                  variant="neutral"
                  className="font-sans text-xs font-normal normal-case tracking-normal"
                >
                  Invoice {invoiceId}
                  <button
                    type="button"
                    aria-label="Remove invoice filter"
                    className="inline-flex"
                    onClick={() => {
                      void setInvoiceId(null);
                    }}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </Badge>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  CSV export is limited to the most recent 10,000 commissions. Contact support for a
                  full export.
                </TooltipContent>
              </Tooltip>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
                <Download className="mr-2 h-4 w-4" />
                {downloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={commissions}
            loading={loading}
            manualPagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            hideTotalPageCount
            hideToolbar
            onRowClicked={(row) => {
              setSelectedCommission(row);
              setSheetOpen(true);
            }}
            onPaginationChange={(pagination) => {
              if (pagination.pageSize !== pageSize) {
                setPageSize(pagination.pageSize);
                return;
              }
              if (pagination.pageIndex > pageIndex) {
                nextPage();
              } else if (pagination.pageIndex < pageIndex) {
                previousPage();
              }
            }}
            emptyState={
              error ? (
                <span className="text-destructive">Failed to load commissions.</span>
              ) : (
                <span className="text-muted-foreground">
                  {invoiceId ? `No commissions for invoice ${invoiceId}` : 'No commissions yet'}
                </span>
              )
            }
          />
        </CardContent>
      </Card>
    </>
  );
};

export default Page;
